import type { DefaultOptionType } from 'antd/es/select';
import keyBy from 'lodash/keyBy';
import { ReactNode } from 'react';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { PolicyBook } from 'types/assets/FixedAssetPolicy';

// TODO: Move to types/data/entity.ts
export interface Entity {
  entity_id: string;
  entity_set_id: string;
  short_code: string;
  entity_name: string;
  entity_type: 'PARENT' | 'SUBSIDIARY';
  parent_entity_id?: string;
  legal_name?: string;
  default_currency?: string;
  accounting_system?: string;
  primary_book?: PolicyBook;
  /** e.g., "US GAAP" for consolidation (null for parent entity) */
  parent_gaap_book?: PolicyBook;
  /** Additional books like tax books, management reporting, etc. */
  additional_books?: PolicyBook[];
}

export interface ConsolidationSet {
  id: string;
  entity_set_id: string;
  name: string;
  short_code?: string;
  default: boolean;
  entities: string[] | null;
}

export type EntitiesMap = { [entityId: string]: Entity | undefined };
export type EntitySetIdMap = { [entitySetId: string]: Entity | undefined };
export type ConsolidationSetsMap = {
  [consolidationId: string]: ConsolidationSet | undefined;
};
export type ConsolidationEntitySetIdMap = {
  [entitySetId: string]: ConsolidationSet | undefined;
};

interface SelectOption extends DefaultOptionType {
  label: ReactNode;
  value: string;
}

type GroupedOption = {
  label: string;
  options: SelectOption[];
};

interface EntityState {
  entities: Entity[] | null;
  entitiesOptions: SelectOption[];
  entitiesObject: EntitiesMap;
  entitySetIdObject: EntitySetIdMap;
  consolidationSets: ConsolidationSet[] | null;
  consolidationSetsOptions: SelectOption[];
  consolidationSetsObject: ConsolidationSetsMap;
  consolidationEntitySetIdObject: ConsolidationEntitySetIdMap;
  groupedEntitiesOptions: GroupedOption[];
  selectedEntity: Entity | null;
  selectedConsolidationSet: ConsolidationSet | null;
  lastSelectedPrefixedValue: string | undefined;
  setEntities: (entities: Entity[]) => void;
  setConsolidationSets: (consolidationSets: ConsolidationSet[]) => void;
  setSelectedEntity: (selection: Entity | null) => void;
  setSelectedConsolidationSet: (selection: ConsolidationSet | null) => void;
  setLastSelectedPrefixedValue: (value: string | undefined) => void;
}

// helper to create entity option
const createEntityOption = (entity: Entity): SelectOption => ({
  label: entity.entity_name,
  value: entity.entity_id,
});

// helper function to compute entities grouped by consolidation sets
const computeGroupedEntitiesOptions = (
  entities: Entity[] | null,
  consolidationSets: ConsolidationSet[] | null
): GroupedOption[] => {
  if (!entities) return [];

  // if no consolidation sets, return all entities as one group
  if (!consolidationSets?.length) {
    return [
      {
        label: 'Entities',
        options: entities.map(createEntityOption),
      },
    ];
  }

  const groups: GroupedOption[] = [];
  const entitiesInSets = new Set<string>();

  // create a map for O(1) lookup
  const entityNameMap = new Map(
    entities.map((entity) => [entity.entity_name, entity])
  );

  consolidationSets.forEach((set) => {
    if (!set.entities?.length) return;

    const entitiesInSet = set.entities
      .map((entityName) => {
        const entity = entityNameMap.get(entityName);
        if (!entity) return null;

        entitiesInSets.add(entity.entity_id);

        return {
          label: entity.entity_name,
          // prefix value with set id to make it unique across sets
          value: `${set.id}__${entity.entity_id}`,
        };
      })
      .filter(
        (option): option is { label: string; value: string } => option !== null
      );

    if (entitiesInSet.length > 0) {
      groups.push({
        label: set.name || `Set ${set.id}`,
        options: entitiesInSet,
      });
    }
  });

  // add ungrouped entities (not in any consolidation set)
  const ungroupedEntities = entities
    .filter((entity) => !entitiesInSets.has(entity.entity_id))
    .map(createEntityOption);

  if (ungroupedEntities.length > 0) {
    groups.push({
      label: 'Other Entities',
      options: ungroupedEntities,
    });
  }

  return groups;
};

const useEntityStore = create<EntityState>()(
  persist(
    (set, get) => ({
      entities: null,
      entitiesOptions: [],
      entitiesObject: {},
      entitySetIdObject: {},
      consolidationSets: null,
      consolidationSetsOptions: [],
      consolidationSetsObject: {},
      consolidationEntitySetIdObject: {},
      groupedEntitiesOptions: [],
      selectedEntity: null,
      selectedConsolidationSet: null,
      lastSelectedPrefixedValue: undefined,

      setEntities: (entities) => {
        const sortedEntities = entities.toSorted((a, b) =>
          a.entity_name.localeCompare(b.entity_name)
        );
        const groupedOptions = computeGroupedEntitiesOptions(
          sortedEntities,
          get().consolidationSets
        );

        set({
          entities: sortedEntities,
          entitiesObject: keyBy(sortedEntities, 'entity_id'),
          entitySetIdObject: keyBy(sortedEntities, 'entity_set_id'),
          entitiesOptions: sortedEntities.map(createEntityOption),
          groupedEntitiesOptions: groupedOptions,
        });
      },

      setConsolidationSets: (consolidationSets) => {
        const sortedConsolidationSets = consolidationSets.toSorted((a, b) =>
          a.name.localeCompare(b.name)
        );
        const groupedOptions = computeGroupedEntitiesOptions(
          get().entities,
          sortedConsolidationSets
        );

        set({
          consolidationSets: sortedConsolidationSets,
          consolidationSetsObject: keyBy(sortedConsolidationSets, 'id'),
          consolidationEntitySetIdObject: keyBy(
            sortedConsolidationSets,
            'entity_set_id'
          ),
          consolidationSetsOptions: sortedConsolidationSets.map((set) => ({
            label: set.name,
            value: set.id,
          })),
          groupedEntitiesOptions: groupedOptions,
        });
      },
      setSelectedEntity: (selection) => set({ selectedEntity: selection }),
      setSelectedConsolidationSet: (selection) =>
        set({ selectedConsolidationSet: selection }),
      setLastSelectedPrefixedValue: (value) =>
        set({ lastSelectedPrefixedValue: value }),
    }),
    {
      name: 'entity-storage',
      storage: createJSONStorage(() => localStorage),
    }
  )
);

export { useEntityStore };
